import React, { useRef, useEffect } from 'react';

interface ProjectCardProps {
    title: string;
    description: string;
    kpis: string[];
    imageUrl: string;
    videoUrl?: string;
}

export const ProjectCard: React.FC<ProjectCardProps> = ({ title, description, kpis, imageUrl, videoUrl }) => {
    const videoRef = useRef<HTMLVideoElement>(null);

    useEffect(() => {
        const video = videoRef.current;
        if (!video) return;

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        // Trigger the browser to load the video source now
                        video.load();
                        video.play().catch(() => {
                            // Autoplay may be blocked; silently ignore
                        });
                        observer.unobserve(video);
                    }
                });
            },
            { threshold: 0.25 }
        );

        observer.observe(video);
        return () => observer.disconnect();
    }, [videoUrl]);

    return (
        <div className="group relative overflow-hidden rounded-lg shadow-lg hover:shadow-2xl transition-shadow duration-300">
            {videoUrl ? (
                <video
                    ref={videoRef}
                    preload="none"
                    poster={imageUrl}
                    loop
                    muted
                    playsInline
                    className="w-full h-80 object-cover transition-transform duration-300 group-hover:scale-105"
                >
                    <source src={videoUrl} type="video/mp4" />
                </video>
            ) : (
                <picture>
                    {imageUrl && <source srcSet={imageUrl.replace(/\.(png|jpe?g)$/i, '.webp')} type="image/webp" />}
                    <img src={imageUrl || 'https://picsum.photos/seed/placeholder/800/600'} alt={title || 'Project image'} className="w-full h-80 object-cover transition-transform duration-300 group-hover:scale-105" loading="lazy" />
                </picture>
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
            <div className="absolute bottom-0 left-0 p-6 text-white">
                <h3 className="text-2xl font-bold">{title || ''}</h3>
                <p className="mt-2 opacity-90">{description || ''}</p>
                <div className="flex flex-wrap gap-2 mt-4">
                    {(kpis || []).map(kpi => (
                        <span key={kpi} className="bg-accent text-accent-foreground text-xs font-semibold px-2.5 py-1 rounded">{kpi}</span>
                    ))}
                </div>
            </div>
        </div>
    );
};